// frontend/src/components/ModernSpecificationViewer.js
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import './ui/ModernSpecificationViewer.css';
import './ui/ModernSpecificationViewer-exports.css';

/**
 * Composant moderne pour afficher une spécification complète générée par l'IA
 * Préserve et met en valeur toute la richesse des données backend
 */
const ModernSpecificationViewer = ({ specification, architecturalContext, exportResults }) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('overview');

  if (!specification) {
    return (
      <div className="spec-viewer-empty">
        <div className="spec-empty-icon">📋</div>
        <div className="spec-empty-text">Aucune spécification disponible</div>
      </div>
    );
  }

  // Accès direct aux données (le backend les envoie déjà structurées)
  const spec = specification.specification || specification;

  // Helper pour parser et afficher les données JSON structurées
  const parseStructuredData = (content) => {
    if (!content) return null;
    
    // Si c'est déjà un objet, le retourner tel quel
    if (typeof content === 'object') return content;
    
    // Si c'est une chaîne qui ressemble à du JSON, essayer de la parser
    if (typeof content === 'string') {
      try {
        // Nettoyer la chaîne et essayer différents formats
        let trimmed = content.trim();
        
        // Cas 1: JSON direct
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
          return JSON.parse(trimmed);
        }
        
        // Cas 2: Format numéroté (1. {JSON}, 2. {JSON}, etc.)
        const numberedPattern = /^\d+\.\s*(\{.*\})\s*$/;
        const match = trimmed.match(numberedPattern);
        if (match) {
          return JSON.parse(match[1]);
        }
        
        // Cas 3: Extraire tous les objets JSON d'une chaîne
        const jsonObjects = [];
        const jsonPattern = /\{[^{}]*(?:\{[^{}]*\}[^{}]*)*\}/g;
        let jsonMatch;
        while ((jsonMatch = jsonPattern.exec(trimmed)) !== null) {
          try {
            jsonObjects.push(JSON.parse(jsonMatch[0]));
          } catch (e) {
            // Ignorer les objets JSON invalides
          }
        }
        
        if (jsonObjects.length === 1) {
          return jsonObjects[0];
        } else if (jsonObjects.length > 1) {
          return jsonObjects;
        }
        
      } catch (e) {
        // Si le parsing échoue, retourner la chaîne telle quelle
      }
    }
    
    return content;
  };

  // Composant pour afficher les critères d'acceptation Gherkin
  const renderAcceptanceCriteria = (criteria) => {
    const parsed = parseStructuredData(criteria);
    
    // Si c'est un tableau d'objets
    if (Array.isArray(parsed)) {
      return (
        <div className="spec-multiple-criteria">
          {parsed.map((item, index) => (
            <div key={index}>
              {renderSingleAcceptanceCriteria(item)}
            </div>
          ))}
        </div>
      );
    }
    
    return renderSingleAcceptanceCriteria(parsed);
  };

  // Fonction helper pour un seul critère d'acceptation
  const renderSingleAcceptanceCriteria = (parsed) => {
    if (parsed && typeof parsed === 'object' && parsed.gherkin) {
      return (
        <div className="spec-gherkin-container">
          <div className="spec-gherkin-header">
            <span className="spec-scenario-id">{parsed.scenarioId}</span>
            <span className={`spec-priority spec-priority-${parsed.priority}`}>{parsed.priority}</span>
          </div>
          <h4 className="spec-gherkin-title">{parsed.title}</h4>
          <div className="spec-gherkin-steps">
            {parsed.gherkin.given && (
              <div className="spec-gherkin-section">
                <strong className="spec-gherkin-keyword">Given:</strong>
                <ul>
                  {parsed.gherkin.given.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ul>
              </div>
            )}
            {parsed.gherkin.when && (
              <div className="spec-gherkin-section">
                <strong className="spec-gherkin-keyword">When:</strong>
                <ul>
                  {parsed.gherkin.when.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ul>
              </div>
            )}
            {parsed.gherkin.then && (
              <div className="spec-gherkin-section">
                <strong className="spec-gherkin-keyword">Then:</strong>
                <ul>
                  {parsed.gherkin.then.map((step, i) => (
                    <li key={i}>{step}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      );
    }
    
    // Fallback pour texte simple
    return <div className="spec-content spec-content-fallback">{String(parsed || 'Non défini')}</div>;
  };

  // Composant pour afficher les cas de test structurés
  const renderTestCase = (test) => {
    const parsed = parseStructuredData(test);
    
    // Si c'est un tableau d'objets
    if (Array.isArray(parsed)) {
      return (
        <div className="spec-multiple-tests">
          {parsed.map((item, index) => (
            <div key={index}>
              {renderSingleTestCase(item)}
            </div>
          ))}
        </div>
      );
    }
    
    return renderSingleTestCase(parsed);
  };

  // Fonction helper pour un seul cas de test
  const renderSingleTestCase = (parsed) => {
    if (parsed && typeof parsed === 'object' && parsed.id) {
      return (
        <div className="spec-test-container">
          <div className="spec-test-header">
            <span className="spec-test-id">{parsed.id}</span>
            {parsed.type && <span className="spec-test-type">{parsed.type}</span>}
          </div>
          <h4 className="spec-test-title">{parsed.title}</h4>
          {parsed.description && (
            <p className="spec-test-description">{parsed.description}</p>
          )}
          
          {parsed.preconditions && parsed.preconditions.length > 0 && (
            <div className="spec-test-section">
              <strong>Préconditions:</strong>
              <ul className="spec-test-preconditions">
                {parsed.preconditions.map((condition, i) => (
                  <li key={i}>{condition}</li>
                ))}
              </ul>
            </div>
          )}
          
          {parsed.steps && parsed.steps.length > 0 && (
            <div className="spec-test-section">
              <strong>Étapes:</strong>
              <ol className="spec-test-steps">
                {parsed.steps.map((step, i) => (
                  <li key={i}>{step}</li>
                ))}
              </ol>
            </div>
          )}
          
          {parsed.expectedResult && (
            <div className="spec-test-result">
              <strong>Résultat attendu:</strong> {parsed.expectedResult}
            </div>
          )}
        </div>
      );
    }
    
    // Fallback pour texte simple
    return <div className="spec-content spec-content-fallback">{String(parsed || 'Non défini')}</div>;
  };

  // Composant pour afficher les risques structurés
  const renderRisk = (risk) => {
    const parsed = parseStructuredData(risk);
    
    // Si c'est un tableau d'objets
    if (Array.isArray(parsed)) {
      return (
        <div className="spec-multiple-risks">
          {parsed.map((item, index) => (
            <div key={index}>
              {renderSingleRisk(item)}
            </div>
          ))}
        </div>
      );
    }
    
    return renderSingleRisk(parsed);
  };

  // Fonction helper pour un seul risque
  const renderSingleRisk = (parsed) => {
    if (parsed && typeof parsed === 'object' && parsed.id) {
      return (
        <div className="spec-risk-container">
          <div className="spec-risk-header">
            <span className="spec-risk-id">{parsed.id}</span>
            {parsed.category && <span className="spec-risk-category">{parsed.category}</span>}
            {parsed.probability && (
              <span className={`spec-risk-badge spec-risk-probability-${parsed.probability.toLowerCase()}`}>
                P: {parsed.probability}
              </span>
            )}
            {parsed.impact && (
              <span className={`spec-risk-badge spec-risk-impact-${parsed.impact.toLowerCase()}`}>
                I: {parsed.impact}
              </span>
            )}
          </div>
          <p className="spec-risk-description">{parsed.description}</p>
          {parsed.mitigation && (
            <div className="spec-risk-mitigation">
              <strong>Mitigation:</strong> {parsed.mitigation}
            </div>
          )}
        </div>
      );
    }
    
    // Fallback pour texte simple
    return <div className="spec-content spec-content-fallback">{String(parsed || 'Non défini')}</div>;
  };

  // Helper pour rendre du contenu avec style approprié
  const renderContent = (content, type = 'text') => {
    if (!content) return <span className="spec-empty-value">Non défini</span>;
    
    // Traitement spécial pour certains types
    if (type === 'acceptance') {
      return renderAcceptanceCriteria(content);
    }
    
    if (type === 'test') {
      return renderTestCase(content);
    }
    
    if (type === 'risk') {
      return renderRisk(content);
    }
    
    if (typeof content === 'string') {
      return <div className={`spec-content spec-content-${type}`}>{content}</div>;
    }
    
    if (Array.isArray(content)) {
      return (
        <ul className={`spec-list spec-list-${type}`}>
          {content.map((item, i) => (
            <li key={i} className="spec-list-item">
              {typeof item === 'string' ? item : renderContent(item, type)}
            </li>
          ))}
        </ul>
      );
    }
    
    if (typeof content === 'object') {
      return (
        <div className="spec-object">
          {Object.entries(content).map(([key, value]) => (
            <div key={key} className="spec-object-item">
              <strong className="spec-object-key">{key}:</strong>
              <span className="spec-object-value">{renderContent(value, type)}</span>
            </div>
          ))}
        </div>
      );
    }
    
    return <span className="spec-content">{String(content)}</span>;
  };

  // Calculer les statistiques pour les badges
  const stats = {
    // Nouvelle structure API
    projectInfo: spec?.project ? 1 : 0,
    stakeholders: (spec?.stakeholders?.length || 0) + (spec?.project?.stakeholders ? Object.keys(spec.project.stakeholders).length : 0),
    functionalRequirements: spec?.feature?.functional_requirements?.length || 0,
    // Compter EXACTEMENT les critères d'acceptation de toutes les exigences
    acceptanceCriteria: spec?.feature?.functional_requirements?.reduce((total, req) => {
      const criteriaCount = req.acceptance_criteria?.length || 0;
      console.log(`🔍 ${req.id}: ${criteriaCount} critères`);
      return total + criteriaCount;
    }, 0) || (spec?.criteres_acceptation?.length || 0),
    // 🚀 Nouvelles données contextuelles
    integrationArchitecture: spec?.integration_architecture ? 1 : 0,
    contextualCriteria: spec?.criteres_acceptation?.length || 0,
    contextualMetadata: spec?.contextualMetadata ? 1 : 0,
    componentsImpacted: spec?.integration_architecture?.composants_impactes?.length || 0,
    newComponents: spec?.integration_architecture?.nouveaux_composants?.length || 0,
    // 🔥 Données depuis les props
    externalArchitecturalContext: architecturalContext ? 1 : 0,
    externalExportResults: exportResults ? Object.keys(exportResults).length : 0,
    // Compatibilité avec l'ancienne structure
    userStories: spec?.userStories?.length || 0,
    functionalReq: spec?.functionalRequirements?.length || 0,
    nonFunctionalReq: spec?.nonFunctionalRequirements?.length || 0,
    testCases: spec?.testCases?.length || 0,
    risks: spec?.risks?.length || 0,
    personas: spec?.personas?.length || 0,
    dependencies: spec?.dependencies?.length || 0
  };

  console.log('📊 Stats calculées:', stats);

  // Définir les onglets avec données disponibles
  const tabs = [
    {
      id: 'overview',
      label: '📋 Vue d\'ensemble',
      always: true
    },
    {
      id: 'project',
      label: `🎯 Projet`,
      show: stats.projectInfo > 0,
      data: spec?.project
    },
    {
      id: 'feature',
      label: `⚙️ Exigences (${stats.functionalRequirements})`,
      show: stats.functionalRequirements > 0,
      data: spec?.feature
    },
    {
      id: 'stakeholders',
      label: `👥 Parties (${stats.stakeholders})`,
      show: stats.stakeholders > 0,
      data: spec?.stakeholders || spec?.project?.stakeholders
    },
    {
      id: 'acceptance',
      label: `✅ Critères (${stats.acceptanceCriteria})`,
      show: stats.acceptanceCriteria > 0,
      data: spec?.feature?.functional_requirements
    },
    {
      id: 'contextualCriteria',
      label: `🧠 Context (${stats.contextualCriteria})`,
      show: stats.contextualCriteria > 0,
      data: spec?.criteres_acceptation
    },
    {
      id: 'integration',
      label: `🔧 Intégration (${stats.componentsImpacted + stats.newComponents})`,
      show: stats.integrationArchitecture > 0,
      data: spec?.integration_architecture
    },
    {
      id: 'contextualMeta',
      label: '📊 Métadonnées',
      show: stats.contextualMetadata > 0,
      data: spec?.contextualMetadata
    },
    {
      id: 'architecturalAnalysis',
      label: '🏗️ Architecture',
      show: stats.externalArchitecturalContext > 0,
      data: architecturalContext
    },
    {
      id: 'exportsResults',
      label: `📤 Exports (${stats.externalExportResults})`,
      show: stats.externalExportResults > 0,
      data: exportResults
    },
    {
      id: 'business',
      label: '💼 Business',
      show: spec?.businessValue,
      data: spec?.businessValue
    },
    {
      id: 'userStories',
      label: `📖 Stories (${stats.userStories})`,
      show: stats.userStories > 0,
      data: spec?.userStories
    },
    {
      id: 'personas',
      label: `� Personas (${stats.personas})`,
      show: stats.personas > 0,
      data: spec?.personas
    },
    {
      id: 'functionalReq',
      label: `⚙️ Fonct. (${stats.functionalReq})`,
      show: stats.functionalReq > 0,
      data: spec?.functionalRequirements
    },
    {
      id: 'nonFunctionalReq',
      label: `🔧 Non-Fonct. (${stats.nonFunctionalReq})`,
      show: stats.nonFunctionalReq > 0,
      data: spec?.nonFunctionalRequirements
    },
    {
      id: 'tests',
      label: `🧪 Tests (${stats.testCases})`,
      show: stats.testCases > 0,
      data: spec?.testCases
    },
    {
      id: 'risks',
      label: `⚠️ Risques (${stats.risks})`,
      show: stats.risks > 0,
      data: spec?.risks
    },
    {
      id: 'dependencies',
      label: `🔗 Dépend. (${stats.dependencies})`,
      show: stats.dependencies > 0,
      data: spec?.dependencies
    },
    {
      id: 'dod',
      label: '✔️ DoD',
      show: spec?.definitionOfDone?.length > 0,
      data: spec?.definitionOfDone
    },
    {
      id: 'recommendations',
      label: '💡 Recomm.',
      show: specification?.recommendations?.length > 0,
      data: specification?.recommendations
    },
    {
      id: 'quality',
      label: '📊 Qualité',
      show: specification?.qualityScore,
      data: specification?.qualityScore
    }
  ];

  const visibleTabs = tabs.filter(tab => tab.always || tab.show);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="spec-tab-content">
            <div className="spec-header">
              <h1 className="spec-title">{spec?.title || 'Spécification Générée'}</h1>
              <div className="spec-badges">
                {specification?.metadata?.generatedAt && (
                  <div className="spec-badge spec-badge-date">
                    📅 {new Date(specification.metadata.generatedAt).toLocaleDateString('fr-FR')}
                  </div>
                )}
                {specification?.metadata?.version && (
                  <div className="spec-badge spec-badge-version">
                    🔢 v{specification.metadata.version}
                  </div>
                )}
              </div>
            </div>

            {spec?.summary && (
              <div className="spec-card spec-card-primary">
                <h3 className="spec-card-title">📋 Résumé Exécutif</h3>
                {renderContent(spec.summary, 'summary')}
              </div>
            )}

            {spec?.functionalDescription && (
              <div className="spec-card">
                <h3 className="spec-card-title">📝 Description Fonctionnelle</h3>
                {renderContent(spec.functionalDescription, 'description')}
              </div>
            )}

            {spec?.estimations && (
              <div className="spec-card">
                <h3 className="spec-card-title">📊 Estimations</h3>
                <div className="spec-estimations">
                  {spec.estimations.complexity && (
                    <div className="spec-estimation-item">
                      <span className="spec-estimation-label">Complexité:</span>
                      <span className={`spec-estimation-value spec-complexity-${spec.estimations.complexity?.toLowerCase()}`}>
                        {spec.estimations.complexity}
                      </span>
                    </div>
                  )}
                  {spec.estimations.effort && (
                    <div className="spec-estimation-item">
                      <span className="spec-estimation-label">Effort:</span>
                      <span className="spec-estimation-value">{spec.estimations.effort}</span>
                    </div>
                  )}
                  {spec.estimations.timeline && (
                    <div className="spec-estimation-item">
                      <span className="spec-estimation-label">Timeline:</span>
                      <span className="spec-estimation-value">{spec.estimations.timeline}</span>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Statistiques globales */}
            <div className="spec-card">
              <h3 className="spec-card-title">📈 Aperçu Global</h3>
              <div className="spec-stats-grid">
                <div className="spec-stat">
                  <div className="spec-stat-number">{stats.userStories}</div>
                  <div className="spec-stat-label">User Stories</div>
                </div>
                <div className="spec-stat">
                  <div className="spec-stat-number">{stats.functionalRequirements}</div>
                  <div className="spec-stat-label">Exigences Fonct.</div>
                </div>
                <div className="spec-stat">
                  <div className="spec-stat-number">{stats.acceptanceCriteria}</div>
                  <div className="spec-stat-label">Critères Accept.</div>
                </div>
                <div className="spec-stat">
                  <div className="spec-stat-number">{stats.testCases}</div>
                  <div className="spec-stat-label">Tests</div>
                </div>
                <div className="spec-stat">
                  <div className="spec-stat-number">{stats.risks}</div>
                  <div className="spec-stat-label">Risques</div>
                </div>
                {/* 🚀 Nouvelles statistiques contextuelles */}
                {stats.contextualCriteria > 0 && (
                  <div className="spec-stat spec-stat-contextual">
                    <div className="spec-stat-number">{stats.contextualCriteria}</div>
                    <div className="spec-stat-label">Critères Contextuels</div>
                  </div>
                )}
                {stats.componentsImpacted > 0 && (
                  <div className="spec-stat spec-stat-contextual">
                    <div className="spec-stat-number">{stats.componentsImpacted}</div>
                    <div className="spec-stat-label">Composants Impactés</div>
                  </div>
                )}
                {stats.newComponents > 0 && (
                  <div className="spec-stat spec-stat-contextual">
                    <div className="spec-stat-number">{stats.newComponents}</div>
                    <div className="spec-stat-label">Nouveaux Composants</div>
                  </div>
                )}
              </div>
            </div>

            {/* 🧠 Aperçu contextuel si disponible */}
            {spec.contextualMetadata && (
              <div className="spec-card spec-card-contextual">
                <h3 className="spec-card-title">🧠 Analyse Contextuelle</h3>
                <div className="spec-contextual-summary">
                  <div className="spec-contextual-item">
                    <span className="spec-contextual-label">Style architectural:</span>
                    <span className="spec-contextual-value">{spec.contextualMetadata.architecturalContext?.architecturalStyle || 'N/A'}</span>
                  </div>
                  <div className="spec-contextual-item">
                    <span className="spec-contextual-label">Nœuds analysés:</span>
                    <span className="spec-contextual-value">{spec.contextualMetadata.architecturalContext?.totalExistingNodes || 0}</span>
                  </div>
                  <div className="spec-contextual-item">
                    <span className="spec-contextual-label">Dépendances identifiées:</span>
                    <span className="spec-contextual-value">{spec.contextualMetadata.architecturalContext?.identifiedDependencies || 0}</span>
                  </div>
                  <div className="spec-contextual-item">
                    <span className="spec-contextual-label">Conflits potentiels:</span>
                    <span className="spec-contextual-value">{spec.contextualMetadata.architecturalContext?.potentialConflicts || 0}</span>
                  </div>
                </div>
              </div>
            )}

            {/* 🎯 Nouvelle fonctionnalité si définie */}
            {spec.nouvelle_fonctionnalite && (
              <div className="spec-card spec-card-feature">
                <h3 className="spec-card-title">🎯 Nouvelle Fonctionnalité</h3>
                <div className="spec-feature-name">{spec.nouvelle_fonctionnalite}</div>
                {spec.description && (
                  <div className="spec-feature-description">{spec.description}</div>
                )}
              </div>
            )}
          </div>
        );

      case 'project':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🎯 Projet</h2>
            <div className="spec-project-grid">
              <div className="spec-card">
                <h3 className="spec-card-title">📝 Description du Projet</h3>
                <div className="spec-project-name">{spec.project?.name}</div>
                <div className="spec-project-description">{spec.project?.description}</div>
              </div>
              {/* Afficher les goals seulement s'ils existent */}
              {spec.project?.goals?.length > 0 && (
                <div className="spec-card">
                  <h3 className="spec-card-title">🎯 Objectifs</h3>
                  <ul className="spec-goals-list">
                    {spec.project.goals.map((goal, index) => (
                      <li key={index} className="spec-goal-item">{goal}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        );

      case 'feature':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">⚙️ Feature & Exigences</h2>
            <div className="spec-feature-grid">
              <div className="spec-card">
                <h3 className="spec-card-title">🔧 Feature</h3>
                <div className="spec-feature-name">{spec.feature?.name}</div>
                <div className="spec-feature-description">{spec.feature?.description}</div>
              </div>
              {spec.feature?.functional_requirements?.length > 0 && (
                <div className="spec-card">
                  <h3 className="spec-card-title">📋 Exigences Fonctionnelles</h3>
                  <div className="spec-requirements-list">
                    {spec.feature.functional_requirements.map((req, index) => (
                      <div key={req.id || index} className="spec-requirement-item">
                        <div className="spec-requirement-header">
                          <span className="spec-requirement-id">{req.id}</span>
                          <h4 className="spec-requirement-title">{req.description}</h4>
                        </div>
                        {req.acceptance_criteria?.length > 0 && (
                          <div className="spec-acceptance-criteria">
                            <h5>Critères d'acceptation :</h5>
                            <ul>
                              {req.acceptance_criteria.map((criteria, cIndex) => (
                                <li key={cIndex}>{criteria}</li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      case 'stakeholders':
        const stakeholdersData = spec?.stakeholders || spec?.project?.stakeholders;
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">👥 Parties Prenantes</h2>
            <div className="spec-stakeholders-grid">
              {/* Nouvelle structure : array de stakeholders */}
              {Array.isArray(stakeholdersData) && stakeholdersData.map((stakeholder, index) => (
                <div key={index} className="spec-card spec-stakeholder-card">
                  <h3 className="spec-card-title">{stakeholder.role?.toUpperCase()}</h3>
                  <div className="spec-stakeholder-info">
                    {stakeholder.name && <div><strong>Nom:</strong> {stakeholder.name}</div>}
                    {stakeholder.responsibilities && <div><strong>Responsabilités:</strong> {stakeholder.responsibilities}</div>}
                  </div>
                </div>
              ))}
              {/* Ancienne structure : object de stakeholders */}
              {!Array.isArray(stakeholdersData) && stakeholdersData && Object.entries(stakeholdersData).map(([role, person]) => (
                <div key={role} className="spec-card spec-stakeholder-card">
                  <h3 className="spec-card-title">{role.replace('_', ' ').toUpperCase()}</h3>
                  <div className="spec-stakeholder-info">{person}</div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'acceptance':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">✅ Critères d'Acceptation</h2>
            <div className="spec-acceptance-grid">
              {spec?.feature?.functional_requirements?.map((req, index) => {
                return req.acceptance_criteria?.length > 0 ? (
                  <div key={req.id || index} className="spec-card">
                    <h3 className="spec-card-title">{req.id} - {req.description}</h3>
                    <div className="spec-acceptance-list">
                      {req.acceptance_criteria.map((criteria, cIndex) => (
                        <div key={cIndex} className="spec-acceptance-item">
                          {renderContent(criteria, 'acceptance')}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })}
            </div>
          </div>
        );

      case 'contextualCriteria':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🧠 Critères Contextuels</h2>
            <div className="spec-contextual-criteria-grid">
              {spec?.criteres_acceptation?.map((criteria, index) => (
                <div key={index} className="spec-card">
                  <div className="spec-contextual-criteria-item">
                    {renderContent(criteria, 'acceptance')}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'business':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">💼 Valeur Business</h2>
            <div className="spec-business-grid">
              {spec.businessValue?.objectives && (
                <div className="spec-card">
                  <h3 className="spec-card-title">🎯 Objectifs</h3>
                  {renderContent(spec.businessValue.objectives, 'objectives')}
                </div>
              )}
              {spec.businessValue?.kpis && (
                <div className="spec-card">
                  <h3 className="spec-card-title">📊 KPIs</h3>
                  {renderContent(spec.businessValue.kpis, 'kpis')}
                </div>
              )}
              {spec.businessValue?.roi && (
                <div className="spec-card">
                  <h3 className="spec-card-title">💰 ROI</h3>
                  {renderContent(spec.businessValue.roi, 'roi')}
                </div>
              )}
            </div>
          </div>
        );

      case 'userStories':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">📖 User Stories</h2>
            <div className="spec-items-grid">
              {spec.userStories?.map((story, index) => (
                <div key={index} className="spec-card spec-card-story">
                  <div className="spec-item-header">
                    <span className="spec-item-id">{story.id || `US-${index + 1}`}</span>
                    {story.priority && (
                      <span className={`spec-priority spec-priority-${story.priority?.toLowerCase()}`}>
                        {story.priority}
                      </span>
                    )}
                  </div>
                  <h3 className="spec-item-title">{story.title}</h3>
                  <div className="spec-story-content">{story.story}</div>
                  {story.estimation && (
                    <div className="spec-estimation">
                      <strong>Estimation:</strong> {story.estimation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'personas':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">👥 Personas</h2>
            <div className="spec-items-grid">
              {spec.personas?.map((persona, index) => (
                <div key={index} className="spec-card spec-card-persona">
                  <h3 className="spec-item-title">{persona.name}</h3>
                  <div className="spec-persona-role">{persona.role}</div>
                  {persona.needs && (
                    <div className="spec-persona-section">
                      <strong>Besoins:</strong>
                      {renderContent(persona.needs, 'needs')}
                    </div>
                  )}
                  {persona.painPoints && (
                    <div className="spec-persona-section">
                      <strong>Points de douleur:</strong>
                      {renderContent(persona.painPoints, 'painPoints')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'functionalReq':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">⚙️ Exigences Fonctionnelles</h2>
            <div className="spec-requirements-list">
              {spec.functionalRequirements?.map((req, index) => (
                <div key={index} className="spec-card spec-card-requirement">
                  <div className="spec-item-header">
                    <span className="spec-item-id">{req.id || `FR-${index + 1}`}</span>
                    {req.priority && (
                      <span className={`spec-priority spec-priority-${req.priority?.toLowerCase()}`}>
                        {req.priority}
                      </span>
                    )}
                    {req.category && (
                      <span className="spec-category">{req.category}</span>
                    )}
                  </div>
                  <h3 className="spec-item-title">{req.title}</h3>
                  {renderContent(req.description, 'requirement')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'nonFunctionalReq':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🔧 Exigences Non-Fonctionnelles</h2>
            <div className="spec-requirements-list">
              {spec.nonFunctionalRequirements?.map((req, index) => (
                <div key={index} className="spec-card spec-card-nfr">
                  <div className="spec-item-header">
                    <span className="spec-item-id">{req.id || `NFR-${index + 1}`}</span>
                    <span className="spec-category">{req.category}</span>
                  </div>
                  {renderContent(req.description, 'nfr')}
                  {req.criteria && (
                    <div className="spec-criteria">
                      <strong>Critères:</strong> {req.criteria}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'tests':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🧪 Cas de Test</h2>
            <div className="spec-tests-list">
              {spec.testCases?.map((test, index) => (
                <div key={index} className="spec-card spec-card-test">
                  {renderContent(test, 'test')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'risks':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">⚠️ Risques</h2>
            <div className="spec-risks-list">
              {spec.risks?.map((risk, index) => (
                <div key={index} className="spec-card spec-card-risk">
                  {renderContent(risk, 'risk')}
                </div>
              ))}
            </div>
          </div>
        );

      case 'dependencies':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🔗 Dépendances</h2>
            <div className="spec-dependencies-list">
              {spec.dependencies?.map((dep, index) => (
                <div key={index} className="spec-card spec-card-dependency">
                  <div className="spec-item-header">
                    <span className="spec-item-id">D-{index + 1}</span>
                    <span className="spec-category">{dep.type}</span>
                  </div>
                  {renderContent(dep.description, 'dependency')}
                  {dep.impact && (
                    <div className="spec-impact">
                      <strong>Impact:</strong> {dep.impact}
                    </div>
                  )}
                  {dep.owner && (
                    <div className="spec-owner">
                      <strong>Responsable:</strong> {dep.owner}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'dod':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">✔️ Definition of Done</h2>
            <div className="spec-card spec-card-dod">
              {renderContent(spec.definitionOfDone, 'dod')}
            </div>
          </div>
        );

      case 'recommendations':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">💡 Recommandations</h2>
            <div className="spec-card spec-card-recommendations">
              {renderContent(specification.recommendations, 'recommendations')}
            </div>
          </div>
        );

      case 'quality':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">📊 Score de Qualité</h2>
            <div className="spec-card spec-card-quality">
              <div className="spec-quality-scores">
                {specification.qualityScore?.completeness !== undefined && (
                  <div className="spec-quality-item">
                    <span className="spec-quality-label">Complétude</span>
                    <div className="spec-quality-bar">
                      <div 
                        className="spec-quality-fill" 
                        style={{width: `${specification.qualityScore.completeness}%`}}
                      ></div>
                    </div>
                    <span className="spec-quality-value">{specification.qualityScore.completeness}%</span>
                  </div>
                )}
                {specification.qualityScore?.clarity !== undefined && (
                  <div className="spec-quality-item">
                    <span className="spec-quality-label">Clarté</span>
                    <div className="spec-quality-bar">
                      <div 
                        className="spec-quality-fill" 
                        style={{width: `${specification.qualityScore.clarity}%`}}
                      ></div>
                    </div>
                    <span className="spec-quality-value">{specification.qualityScore.clarity}%</span>
                  </div>
                )}
                {specification.qualityScore?.feasibility !== undefined && (
                  <div className="spec-quality-item">
                    <span className="spec-quality-label">Faisabilité</span>
                    <div className="spec-quality-bar">
                      <div 
                        className="spec-quality-fill" 
                        style={{width: `${specification.qualityScore.feasibility}%`}}
                      ></div>
                    </div>
                    <span className="spec-quality-value">{specification.qualityScore.feasibility}%</span>
                  </div>
                )}
                {specification.qualityScore?.overall !== undefined && (
                  <div className="spec-quality-item spec-quality-overall">
                    <span className="spec-quality-label">Score Global</span>
                    <div className="spec-quality-bar">
                      <div 
                        className="spec-quality-fill spec-quality-fill-overall" 
                        style={{width: `${specification.qualityScore.overall}%`}}
                      ></div>
                    </div>
                    <span className="spec-quality-value spec-quality-value-overall">
                      {specification.qualityScore.overall}%
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        );

      // 🚀 NOUVEAUX ONGLETS CONTEXTUELS
      case 'contextualCriteria':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🧠 Critères d'Acceptation Contextuels</h2>
            <div className="spec-contextual-criteria-list">
              {spec.criteres_acceptation?.map((criteria, index) => (
                <div key={index} className="spec-card spec-card-contextual-criteria">
                  <div className="spec-item-header">
                    <span className="spec-item-id">AC-{index + 1}</span>
                  </div>
                  <h3 className="spec-item-title">{criteria.description}</h3>
                  <div className="spec-test-details">
                    <strong>Test:</strong> {criteria.test}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );

      case 'integration':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🔧 Intégration Architecture</h2>
            
            {/* Point d'entrée */}
            {spec.integration_architecture?.point_d_entree && (
              <div className="spec-card spec-integration-entry">
                <h3 className="spec-card-title">🎯 Point d'Entrée Principal</h3>
                <div className="spec-entry-point">
                  <strong>{spec.integration_architecture.point_d_entree}</strong>
                </div>
              </div>
            )}

            {/* Composants impactés */}
            {spec.integration_architecture?.composants_impactes?.length > 0 && (
              <div className="spec-card spec-integration-impacted">
                <h3 className="spec-card-title">🔄 Composants Impactés ({spec.integration_architecture.composants_impactes.length})</h3>
                <div className="spec-impacted-components">
                  {spec.integration_architecture.composants_impactes.map((component, index) => (
                    <div key={index} className="spec-component-item">
                      <div className="spec-component-header">
                        <h4 className="spec-component-name">{component.nom}</h4>
                      </div>
                      <div className="spec-component-role">
                        <strong>Rôle:</strong> {component.role}
                      </div>
                      {component.modifications?.length > 0 && (
                        <div className="spec-component-modifications">
                          <strong>Modifications:</strong>
                          <ul>
                            {component.modifications.map((mod, modIndex) => (
                              <li key={modIndex}>{mod}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Nouveaux composants */}
            {spec.integration_architecture?.nouveaux_composants?.length > 0 && (
              <div className="spec-card spec-integration-new">
                <h3 className="spec-card-title">🆕 Nouveaux Composants ({spec.integration_architecture.nouveaux_composants.length})</h3>
                <div className="spec-new-components">
                  {spec.integration_architecture.nouveaux_composants.map((component, index) => (
                    <div key={index} className="spec-component-item spec-new-component">
                      <div className="spec-component-header">
                        <h4 className="spec-component-name">{component.nom}</h4>
                        <span className="spec-new-badge">NOUVEAU</span>
                      </div>
                      <div className="spec-component-role">
                        <strong>Rôle:</strong> {component.role}
                      </div>
                      {component.relations?.length > 0 && (
                        <div className="spec-component-relations">
                          <strong>Relations:</strong>
                          <ul>
                            {component.relations.map((relation, relIndex) => (
                              <li key={relIndex}>{relation}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      case 'contextualMeta':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">📊 Métadonnées Contextuelles</h2>
            
            {/* Contexte architectural */}
            {spec.contextualMetadata?.architecturalContext && (
              <div className="spec-card spec-architectural-context">
                <h3 className="spec-card-title">🏗️ Contexte Architectural</h3>
                <div className="spec-arch-stats">
                  <div className="spec-arch-stat">
                    <span className="spec-arch-label">Nœuds existants:</span>
                    <span className="spec-arch-value">{spec.contextualMetadata.architecturalContext.totalExistingNodes}</span>
                  </div>
                  <div className="spec-arch-stat">
                    <span className="spec-arch-label">Style architectural:</span>
                    <span className="spec-arch-value">{spec.contextualMetadata.architecturalContext.architecturalStyle}</span>
                  </div>
                  <div className="spec-arch-stat">
                    <span className="spec-arch-label">Dépendances identifiées:</span>
                    <span className="spec-arch-value">{spec.contextualMetadata.architecturalContext.identifiedDependencies}</span>
                  </div>
                  <div className="spec-arch-stat">
                    <span className="spec-arch-label">Conflits potentiels:</span>
                    <span className="spec-arch-value">{spec.contextualMetadata.architecturalContext.potentialConflicts}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Points d'intégration */}
            {spec.contextualMetadata?.integrationPoints?.length > 0 && (
              <div className="spec-card spec-integration-points">
                <h3 className="spec-card-title">🔗 Points d'Intégration</h3>
                <div className="spec-integration-categories">
                  {spec.contextualMetadata.integrationPoints.map((point, index) => (
                    <div key={index} className="spec-integration-category">
                      <h4 className="spec-category-name">{point.category}</h4>
                      <div className="spec-category-nodes">
                        {point.nodes.map((node, nodeIndex) => (
                          <span key={nodeIndex} className="spec-node-tag">{node}</span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Avertissements de conflits */}
            {spec.contextualMetadata?.conflictWarnings?.length > 0 && (
              <div className="spec-card spec-conflict-warnings">
                <h3 className="spec-card-title">⚠️ Avertissements de Conflits</h3>
                <div className="spec-conflict-list">
                  {spec.contextualMetadata.conflictWarnings.map((warning, index) => (
                    <div key={index} className={`spec-conflict-item spec-conflict-${warning.severity}`}>
                      <div className="spec-conflict-type">{warning.type}</div>
                      <div className="spec-conflict-message">{warning.message}</div>
                      <div className="spec-conflict-severity">
                        Sévérité: <span className={`spec-severity-${warning.severity}`}>{warning.severity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analyse timestamp */}
            {spec.contextualMetadata?.analysisTimestamp && (
              <div className="spec-card spec-analysis-meta">
                <h3 className="spec-card-title">⏰ Informations d'Analyse</h3>
                <div className="spec-analysis-timestamp">
                  <strong>Analysé le:</strong> {new Date(spec.contextualMetadata.analysisTimestamp).toLocaleString('fr-FR')}
                </div>
              </div>
            )}
          </div>
        );

      // 🏗️ ANALYSE ARCHITECTURALE EXTERNE
      case 'architecturalAnalysis':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">🏗️ Analyse Architecturale Complète</h2>
            
            {/* Nœuds existants analysés */}
            {architecturalContext?.existingNodes?.length > 0 && (
              <div className="spec-card spec-existing-nodes">
                <h3 className="spec-card-title">📦 Nœuds Existants Analysés ({architecturalContext.existingNodes.length})</h3>
                <div className="spec-nodes-grid">
                  {architecturalContext.existingNodes.slice(0, 10).map((node, index) => (
                    <div key={index} className="spec-node-item">
                      <div className="spec-node-header">
                        <h4 className="spec-node-name">{node.name}</h4>
                        <span className="spec-node-type">{node.type}</span>
                      </div>
                      <div className="spec-node-description">{node.description}</div>
                    </div>
                  ))}
                  {architecturalContext.existingNodes.length > 10 && (
                    <div className="spec-nodes-more">
                      ... et {architecturalContext.existingNodes.length - 10} autres nœuds
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Patterns architecturaux */}
            {architecturalContext?.patterns && (
              <div className="spec-card spec-arch-patterns">
                <h3 className="spec-card-title">🏛️ Patterns Architecturaux</h3>
                <div className="spec-patterns-summary">
                  <div className="spec-pattern-item">
                    <span className="spec-pattern-label">Style:</span>
                    <span className="spec-pattern-value">{architecturalContext.patterns.architecturalStyle}</span>
                  </div>
                  <div className="spec-pattern-item">
                    <span className="spec-pattern-label">Total nœuds:</span>
                    <span className="spec-pattern-value">{architecturalContext.patterns.totalNodes}</span>
                  </div>
                  <div className="spec-pattern-item">
                    <span className="spec-pattern-label">Complexité:</span>
                    <span className="spec-pattern-value">{architecturalContext.architecturalSummary?.complexity}</span>
                  </div>
                </div>
              </div>
            )}

            {/* Dépendances potentielles */}
            {architecturalContext?.potentialDependencies?.length > 0 && (
              <div className="spec-card spec-potential-dependencies">
                <h3 className="spec-card-title">🔗 Dépendances Potentielles ({architecturalContext.potentialDependencies.length})</h3>
                <div className="spec-dependencies-analysis">
                  {architecturalContext.potentialDependencies.map((dep, index) => (
                    <div key={index} className="spec-dependency-analysis">
                      <div className="spec-dependency-header">
                        <h4 className="spec-dependency-category">{dep.category}</h4>
                      </div>
                      <div className="spec-dependency-reason">{dep.reason}</div>
                      <div className="spec-dependency-nodes">
                        <strong>Nœuds concernés:</strong>
                        <div className="spec-nodes-tags">
                          {dep.nodes?.map((node, nodeIndex) => (
                            <span key={nodeIndex} className="spec-node-tag">
                              {node.name || node}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Conflits potentiels */}
            {architecturalContext?.potentialConflicts?.length > 0 && (
              <div className="spec-card spec-potential-conflicts">
                <h3 className="spec-card-title">⚠️ Conflits Potentiels ({architecturalContext.potentialConflicts.length})</h3>
                <div className="spec-conflicts-analysis">
                  {architecturalContext.potentialConflicts.map((conflict, index) => (
                    <div key={index} className={`spec-conflict-analysis spec-conflict-${conflict.severity}`}>
                      <div className="spec-conflict-type-analysis">{conflict.type}</div>
                      <div className="spec-conflict-message-analysis">{conflict.message}</div>
                      {conflict.node && (
                        <div className="spec-conflict-node">
                          <strong>Nœud concerné:</strong> {conflict.node.name} ({conflict.node.type})
                        </div>
                      )}
                      <div className="spec-conflict-severity-analysis">
                        Sévérité: <span className={`spec-severity-${conflict.severity}`}>{conflict.severity}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );

      // 📤 RÉSULTATS D'EXPORTS
      case 'exportsResults':
        return (
          <div className="spec-tab-content">
            <h2 className="spec-section-title">📤 Résultats d'Exports</h2>
            
            {/* Export JIRA */}
            {exportResults?.jira && (
              <div className="spec-card spec-export-jira">
                <h3 className="spec-card-title">📝 Export JIRA</h3>
                <div className="spec-export-details">
                  <div className="spec-export-meta">
                    <div className="spec-export-item">
                      <span className="spec-export-label">Type:</span>
                      <span className="spec-export-value">{exportResults.jira.epic?.issueType || 'Epic'}</span>
                    </div>
                    <div className="spec-export-item">
                      <span className="spec-export-label">Résumé:</span>
                      <span className="spec-export-value">{exportResults.jira.epic?.summary || 'N/A'}</span>
                    </div>
                    <div className="spec-export-item">
                      <span className="spec-export-label">Priorité:</span>
                      <span className="spec-export-value">{exportResults.jira.epic?.priority || 'Medium'}</span>
                    </div>
                    <div className="spec-export-item">
                      <span className="spec-export-label">Sous-tâches:</span>
                      <span className="spec-export-value">{exportResults.jira.subtasks?.length || 0}</span>
                    </div>
                  </div>
                  
                  <div className="spec-export-content">
                    <h4>Description JIRA:</h4>
                    <div className="spec-jira-description">
                      {exportResults.jira.epic?.description || 'Aucune description disponible'}
                    </div>
                  </div>

                  {exportResults.jira.epic?.labels?.length > 0 && (
                    <div className="spec-export-labels">
                      <h4>Labels:</h4>
                      <div className="spec-labels-tags">
                        {exportResults.jira.epic.labels.map((label, index) => (
                          <span key={index} className="spec-label-tag">{label}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  {exportResults.jira.subtasks?.length > 0 && (
                    <div className="spec-export-subtasks">
                      <h4>Sous-tâches générées:</h4>
                      <div className="spec-subtasks-list">
                        {exportResults.jira.subtasks.slice(0, 5).map((subtask, index) => (
                          <div key={index} className="spec-subtask-item">
                            <span className="spec-subtask-type">[{subtask.issueType}]</span>
                            <span className="spec-subtask-summary">{subtask.summary}</span>
                            <span className="spec-subtask-priority">{subtask.priority}</span>
                          </div>
                        ))}
                        {exportResults.jira.subtasks.length > 5 && (
                          <div className="spec-subtask-more">
                            ... et {exportResults.jira.subtasks.length - 5} autres sous-tâches
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Export Confluence */}
            {exportResults?.confluence && (
              <div className="spec-card spec-export-confluence">
                <h3 className="spec-card-title">📄 Export Confluence</h3>
                <div className="spec-export-details">
                  <div className="spec-export-meta">
                    <div className="spec-export-item">
                      <span className="spec-export-label">Titre:</span>
                      <span className="spec-export-value">{exportResults.confluence.title || 'Page Confluence'}</span>
                    </div>
                    {exportResults.confluence.metadata && (
                      <div className="spec-export-item">
                        <span className="spec-export-label">Sections incluses:</span>
                        <span className="spec-export-value">{exportResults.confluence.metadata.sectionsIncluded?.length || 0}</span>
                      </div>
                    )}
                  </div>

                  {exportResults.confluence.metadata?.sectionsIncluded?.length > 0 && (
                    <div className="spec-export-sections">
                      <h4>Sections générées:</h4>
                      <div className="spec-sections-list">
                        {exportResults.confluence.metadata.sectionsIncluded.map((section, index) => (
                          <span key={index} className="spec-section-tag">{section.replace('_', ' ')}</span>
                        ))}
                      </div>
                    </div>
                  )}

                  <div className="spec-export-content">
                    <h4>Contenu Confluence:</h4>
                    <div className="spec-confluence-preview">
                      <div className="spec-confluence-content" dangerouslySetInnerHTML={{
                        __html: exportResults.confluence.content?.substring(0, 1000) + (exportResults.confluence.content?.length > 1000 ? '...' : '')
                      }} />
                      {exportResults.confluence.content?.length > 1000 && (
                        <div className="spec-confluence-more">
                          ... ({Math.round(exportResults.confluence.content.length / 1000)}KB de contenu HTML)
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="spec-tab-content">
            <div className="spec-not-found">
              <div className="spec-not-found-icon">🔍</div>
              <div className="spec-not-found-text">Section non trouvée</div>
            </div>
          </div>
        );
    }
  };

  return (
    <div className="modern-spec-viewer">
      {/* Navigation par onglets */}
      <div className="spec-tabs">
        {visibleTabs.map(tab => (
          <button
            key={tab.id}
            className={`spec-tab ${activeTab === tab.id ? 'spec-tab-active' : ''}`}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Contenu de l'onglet actif */}
      <div className="spec-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default ModernSpecificationViewer;